import { useMemo, useState } from "react";
import CrudPage from "@/components/CrudPage";
import { api, apiErrToStr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Clock, RefreshCw } from "lucide-react";

const splitDays = (v) => Array.isArray(v) ? v.join(",") : (v || "");
const toDays = (v) => String(v || "").split(/[,;\s]+/).map((x) => Number(x)).filter((x) => Number.isInteger(x) && x >= 0 && x <= 6);
export default function TimeConditions() {
  const { user, tenants, activeTenantId } = useAuth();
  const isSuperAdmin = String(user?.role || "").toLowerCase() === "superadmin";
  const [checking, setChecking] = useState("");
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  const fields = useMemo(
    () => [
      ...(isSuperAdmin
        ? [
            {
              key: "tenant_id",
              label: "Cliente",
              type: "select",
              full: true,
              options: [
                { value: "", label: "-- selecione o cliente --" },
                ...tenants.map((tenant) => ({ value: tenant.id, label: tenant.nome })),
              ],
            },
          ]
        : []),
      { key: "nome", label: "Nome" },
      { key: "timezone", label: "Fuso", mono: true },
      { key: "dias_semana", label: "Dias", full: true, mono: true, placeholder: "0,1,2,3,4", hint: "0=segunda, 1=terca, 2=quarta, 3=quinta, 4=sexta, 5=sabado, 6=domingo" },
      { key: "hora_inicio", label: "Inicio", mono: true, placeholder: "08:00" },
      { key: "hora_fim", label: "Fim", mono: true, placeholder: "18:00" },
      { key: "data_inicio", label: "Data inicial", type: "date" },
      { key: "data_fim", label: "Data final", type: "date" },
      { key: "descricao", label: "Descricao", full: true, type: "textarea" },
      { key: "enabled", label: "Ativo", type: "toggle" },
    ],
    [isSuperAdmin, tenants],
  );

  const check = async (row) => {
    setChecking(row.id); setResult(""); setError("");
    try {
      const { data } = await api.get(`/time-conditions/${row.id}/check`);
      setResult(`${row.nome}: ${data.matched ? "dentro do horario" : "fora do horario"}`);
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setChecking("");
    }
  };

  return (
    <div className="space-y-3">
      {result && <div className="text-xs font-mono text-[color:var(--accent-green)]">{result}</div>}
      {error && <div className="text-xs text-[color:var(--accent-red)] font-mono">! {error}</div>}
      <CrudPage
        title="Condicoes de Tempo"
        subtitle="Calendario e horario usados nos DIDs e demais recursos"
        endpoint="/time-conditions"
        testidPrefix="time-conditions"
        fields={fields}
        headers={["Nome", "Dias", "Horario", "Status"]}
        defaultValues={{ tenant_id: activeTenantId || user?.tenant_id || "", nome: "", descricao: "", timezone: "America/Sao_Paulo", dias_semana: "0,1,2,3,4", hora_inicio: "08:00", hora_fim: "18:00", data_inicio: "", data_fim: "", enabled: true }}
        transformFormOpen={(f) => ({ ...f, dias_semana: splitDays(f.dias_semana), data_inicio: f.data_inicio || "", data_fim: f.data_fim || "" })}
        transformSubmit={(f) => ({
          ...f,
          tenant_id: String(f.tenant_id || activeTenantId || user?.tenant_id || "").trim(),
          dias_semana: toDays(f.dias_semana),
          data_inicio: f.data_inicio || null,
          data_fim: f.data_fim || null,
        })}
        onSaved={() => {
          window.dispatchEvent(new Event("voxyra-ipbx-config-pending"));
        }}
        renderRow={(r) => <>
          <td>
            <div className="font-medium">{r.nome}</div>
            <div className="font-mono text-[10px] text-[color:var(--text-muted)]">{r.timezone}</div>
          </td>
          <td className="font-mono text-xs">{(r.dias_semana || []).join(",")}</td>
          <td className="font-mono text-xs">{r.hora_inicio} - {r.hora_fim}</td>
          <td>
            <div className="flex items-center justify-end gap-1">
              <span>{r.enabled ? "Ativo" : "Inativo"}</span>
              <button className="sbc-btn" onClick={() => check(r)} disabled={checking === r.id} title="Verificar agora">
                {checking === r.id ? <RefreshCw size={12} className="animate-spin" /> : <Clock size={12} />}
              </button>
            </div>
          </td>
        </>}
      />
    </div>
  );
}
